import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import Button from '../../../components/ui/Button';
import { Badge } from '../../performance-operations/styles';
import {
  formatNum, isSuppressed, metricByCode, metricText, resultDiff, unitLabel,
} from '../../performance-results/config';

// PM analitik P2 — E10 alt alan: birim tablosu (çalışan kapsamı, sonuç n, metrikler, kıyas dönemi, veri eksikleri ve yönetici).
// Liste = DataTable (istemci tarafı sayfalama; birim sayısı dönem başına sınırlı, sunucu tek seferde toplar). Pano sayfasında
// olduğu için tablo kendi içinde KAYMAZ. Satır → birim çekmecesi; menü: Alt Birimler (hiyerarşi), Çalışan Listesi (E03).
const Wrap = styled.div`
  grid-column: span 12;
  min-width: 0;
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const METRIC_COLUMNS = ['M02', 'M06', 'M09', 'M07', 'M08', 'M51'];

export default function UnitTable({
  data, loading, onOpen, onDrill, employeesLink, onExport, exporting,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const units = data?.units || [];
  const hasCompare = !!data?.comparePeriodId;
  // Sıralama ham sayıyla (gizli hücre en altta kalsın diye null); görüntü metni customBodyRender'da.
  const rows = units.map((u) => {
    const row = {
      key: u.key, unit: u, label: unitLabel(t, u), manager: u.managerName || '', eligibleN: u.eligibleN, resultN: u.resultN, missingN: u.missingN,
      critical: u.critical,
    };
    METRIC_COLUMNS.forEach((c) => {
      const m = metricByCode(u.metrics, c);
      row[c] = m && !isSuppressed(m) && m.value !== null && m.value !== undefined ? Number(m.value) : null;
    });
    const diff = hasCompare ? resultDiff('M06', metricByCode(u.metrics, 'M06'), metricByCode(u.compare, 'M06')) : null;
    row.compare = diff && diff.delta !== undefined ? diff.delta : null;
    row.compareReason = diff?.reason || null;
    return row;
  });
  const unitOf = (meta) => rows[meta.rowIndex]?.unit;

  const columns = [
    {
      name: 'label',
      label: t('performance.results.analytics.unit.col.unit'),
      options: {
        filter: false,
        customBodyRender: (v, meta) => (
          <span>
            {v}
            {rows[meta.rowIndex]?.critical ? <>{' '}<Badge $tone="danger" data-critical-badge="1">{t('performance.orgComparison.criticalFlag')}</Badge></> : null}
          </span>
        ),
      },
    },
    { name: 'manager', label: t('performance.results.analytics.unit.col.manager'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    { name: 'eligibleN', label: t('performance.results.analytics.unit.col.eligibleN'), options: { filter: false, customBodyRender: (v) => formatNum(v, lang, 0) } },
    { name: 'resultN', label: t('performance.results.analytics.unit.col.resultN'), options: { filter: false, customBodyRender: (v) => formatNum(v, lang, 0) } },
    { name: 'missingN', label: t('performance.results.analytics.unit.col.missingN'), options: { filter: false, customBodyRender: (v) => formatNum(v, lang, 0) } },
    ...METRIC_COLUMNS.map((c) => ({
      name: c,
      label: t(`performance.results.analytics.metric.${c}`),
      options: { filter: false, customBodyRender: (v, meta) => metricText(t, metricByCode(unitOf(meta)?.metrics, c), lang) },
    })),
    ...(hasCompare ? [{
      name: 'compare',
      label: t('performance.results.analytics.unit.col.compare'),
      options: {
        filter: false,
        customBodyRender: (v, meta) => {
          if (v !== null && v !== undefined) return `${v > 0 ? '+' : ''}${formatNum(v, lang)}`;
          const reason = rows[meta.rowIndex]?.compareReason;
          return reason ? <Muted>{t(`performance.results.analytics.kpi.reason.${reason}`)}</Muted> : '-';
        },
      },
    }] : []),
    {
      name: 'key',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        viewColumns: false,
        download: false,
        customBodyRender: (v, meta) => {
          const u = unitOf(meta);
          if (!u) return null;
          const list = employeesLink(u);
          return (
            <OptionsMenu
              row={u}
              actions={[
                { type: 'detail', label: t('performance.orgComparison.action.detail'), onClick: () => onOpen(u) },
                { type: 'view', label: t('performance.orgComparison.action.drill'), onClick: () => onDrill(u), condition: (r) => !!r.hasChildren },
                ...(list ? [{ type: 'view', label: t('performance.orgComparison.action.employees'), onClick: () => list() }] : []),
              ]}
            />
          );
        },
      },
    },
  ];

  return (
    <Wrap data-panel="units">
      <DataTable
        title={t('performance.orgComparison.table.title')}
        fetching={loading}
        columns={columns}
        data={rows}
        totalRowCount={rows.length}
        serverSide={false}
        disableFilters
        tableBodyMaxHeight="none"
        customIdSelector="key"
        onRowClick={(row) => onOpen(row.unit)}
        toolbarActions={(
          <Button variant="outline" onClick={onExport} disabled={exporting || rows.length === 0} data-action="export">
            {exporting ? t('common.loading') : t('performance.orgComparison.export')}
          </Button>
        )}
      />
      {data?.hiddenByMinN ? <Muted data-note="minn">{t('performance.orgComparison.table.hiddenByMinN', { count: data.hiddenByMinN, minN: data.minN })}</Muted> : null}
    </Wrap>
  );
}
